import {
    afterRenderEffect,
    ChangeDetectionStrategy,
    Component,
    computed,
    DestroyRef,
    ElementRef,
    inject,
    input,
    linkedSignal,
    output,
    signal,
    viewChild,
} from "@angular/core";
import { S25Bootstrap } from "../../../util/core/s25.bootstrap";
import { UserprefService } from "../../../services/userpref.service";
import { s25Resource } from "../../../util/s25.resource";
import { S25Datefilter } from "../../s25-dateformat/s25.datefilter.service";
import { S25IconComponent } from "../../s25-icon/s25.icon.component";
import {
    aiOutcomeDisplay,
    chatRequest,
    DiagramActionRow,
    DiagramChatRequest,
    DiagramInputRow,
    DiagramPaneContent,
    DiagramPersonRow,
    DiagramSelection,
    executionOutcomeLabel,
    formatResolvedValue,
    inputLabel,
    ObjectNames,
    paneHeading,
    StageTypeStyles,
} from "./workflow.diagram.model";
import { WorkflowDiagramPersonComponent } from "./workflow.diagram.person.component";

export type DiagramPaneMode = "region" | "dialog";

interface PaneRequest {
    method?: string;
    url?: string;
    payload?: string;
}

/** Phone-width viewports, where the detail sheet can be resized from its grabber. */
export const PHONE_MEDIA_QUERY = `(max-width: ${S25Bootstrap.breakpoints.xs - 0.02}px)`;
const SHEET_MIN_HEIGHT_PX = 160;
const SHEET_MIN_FRACTION = 0.3;
/** Keeps a strip of the diagram visible above a fully expanded sheet. */
const SHEET_TOP_GAP_PX = 16;
const SHEET_KEY_STEP_FRACTION = 0.1;
const TAP_SLOP_PX = 4;

const FOCUSABLE_SELECTOR =
    "button:not([disabled]), a[href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex='-1'])";

function urlHost(url: string | undefined): string | undefined {
    if (!url) return undefined;
    try {
        return new URL(url).host || url;
    } catch {
        return url.replace(/^[a-z]+:\/\//i, "").split(/[/?#]/)[0];
    }
}

/**
 * Detail for the selected stage, action, or spawned-form group. Beside the canvas it is a named region;
 * on a narrow viewer it is a modal sheet that traps focus.
 */
@Component({
    selector: "workflow-diagram-detail-pane",
    template: `
        @let pane = content();
        @if (resizable()) {
            <div
                class="sheet-grabber"
                role="separator"
                aria-orientation="horizontal"
                aria-label="Resize details"
                tabindex="0"
                [attr.aria-valuemin]="0"
                [attr.aria-valuemax]="100"
                [attr.aria-valuenow]="sheetPercent()"
                [attr.aria-valuetext]="sheetPercent() + '% of the diagram height'"
                (pointerdown)="onGrabberPointerDown($event)"
                (keydown)="onGrabberKeyDown($event)"
            ></div>
        }
        <div class="pane-header">
            <div class="pane-title">
                @if (pane.kind === "stage") {
                    <p class="pane-eyebrow">
                        <span class="pane-swatch" aria-hidden="true" [style.background]="stageColor()"></span>
                        {{ stageTypeLabel() }} stage
                    </p>
                }
                <h3 #headingElement class="pane-heading" tabindex="-1" [id]="headingId()">{{ heading() }}</h3>
                @switch (pane.kind) {
                    @case ("stage") {
                        @if (pane.stage.statusLabel || pane.stage.flagLabels.length) {
                            <p class="pane-chips">
                                @if (pane.stage.statusLabel) {
                                    <span class="pane-chip" [attr.data-status]="pane.stage.status">
                                        {{ pane.stage.statusLabel }}
                                    </span>
                                }
                                @for (flag of pane.stage.flagLabels; track flag) {
                                    <span class="pane-chip">{{ flag }}</span>
                                }
                            </p>
                        }
                    }
                    @case ("action") {
                        <p class="pane-chips">
                            <span class="pane-chip" [attr.data-mode]="actionIsResolved() ? 'live' : 'configured'">
                                {{ actionIsResolved() ? "This submission" : "Configured mapping" }}
                            </span>
                        </p>
                    }
                    @case ("spawned") {
                        <p class="pane-rollup">
                            <span class="pane-progress" aria-hidden="true">
                                <span
                                    class="pane-progress-fill"
                                    [style.width.%]="
                                        pane.group.forms.length
                                            ? (pane.group.submittedCount / pane.group.forms.length) * 100
                                            : 0
                                    "
                                ></span>
                            </span>
                            {{ pane.group.rollupLabel }}
                        </p>
                    }
                }
            </div>
            <button type="button" class="pane-close" aria-label="Close details" (click)="closed.emit()">
                <s25-ng-icon [type]="'close'" [ariaLabel]="false" />
            </button>
        </div>

        <div class="pane-body">
            @switch (pane.kind) {
                @case ("stage") {
                    @if (pane.stage.states.length) {
                        <h4>States</h4>
                        @for (state of pane.stage.states; track state.id) {
                            <section class="pane-state" [attr.aria-label]="state.name">
                                <h5>{{ state.name }}</h5>
                                <dl class="pane-fields">
                                    @if (state.approvalGroupName) {
                                        <dt>Approval group</dt>
                                        <dd>{{ state.approvalGroupName }}</dd>
                                    }
                                    @if (state.contacts.length) {
                                        <dt>Contacts</dt>
                                        <dd>
                                            @for (contact of state.contacts; track contact.contId; let last = $last) {
                                                {{ contact.name }}{{ last ? "" : ", " }}
                                            }
                                        </dd>
                                    }
                                    @if (state.orgRoleNames.length) {
                                        <dt>Organization roles</dt>
                                        <dd>{{ state.orgRoleNames.join(", ") }}</dd>
                                    }
                                    @if (state.hasAiApprover) {
                                        <dt>AI Approver</dt>
                                        <dd>{{ aiOutcomeDisplay(state.aiOutcome).label }}</dd>
                                    }
                                    @if (
                                        !state.approvalGroupName &&
                                        !state.contacts.length &&
                                        !state.orgRoleNames.length &&
                                        !state.hasAiApprover
                                    ) {
                                        <dt>Approvers</dt>
                                        <dd>None configured</dd>
                                    }
                                </dl>
                            </section>
                        }
                    }

                    @if (pane.stage.actions.length) {
                        <h4>Actions</h4>
                        <ul class="pane-list">
                            @for (action of pane.stage.actions; track action.id) {
                                <li>
                                    <button
                                        type="button"
                                        class="pane-link"
                                        (click)="
                                            selectItem.emit({
                                                kind: 'action',
                                                stageId: pane.stage.id,
                                                actionId: action.id,
                                            })
                                        "
                                    >
                                        <span>{{ action.label }}</span>
                                        <s25-ng-icon [type]="'caretRight'" [ariaLabel]="false" />
                                    </button>
                                </li>
                            }
                        </ul>
                    }

                    @if (pane.stage.submitter; as submitter) {
                        <h4>Submitted by</h4>
                        <workflow-diagram-person
                            [person]="submitter"
                            [chatEnabled]="chatEnabled()"
                            (chat)="emitChat(submitter, $event)"
                        />
                    }

                    @if (pane.stage.approvalRows.length) {
                        <h4>Approvals</h4>
                        <ul class="pane-list">
                            @for (row of pane.stage.approvalRows; track row.key) {
                                <li>
                                    @switch (row.kind) {
                                        @case ("person") {
                                            <workflow-diagram-person
                                                [person]="row"
                                                [chatEnabled]="chatEnabled()"
                                                [showStatus]="live()"
                                                (chat)="emitChat(row, $event)"
                                            />
                                        }
                                        @case ("ai") {
                                            @let ai = aiOutcomeDisplay(row.outcome);
                                            <div class="pane-row">
                                                <s25-ng-icon [type]="'aiSparkles'" [ariaLabel]="false" />
                                                <span class="pane-row-text">
                                                    <strong>
                                                        AI Approver{{ row.stateName ? " — " + row.stateName : "" }}
                                                    </strong>
                                                    <span class="pane-status" [attr.data-tone]="ai.tone">
                                                        <s25-ng-icon [type]="ai.icon" [ariaLabel]="false" />
                                                        {{ ai.label }}
                                                    </span>
                                                </span>
                                            </div>
                                        }
                                        @case ("configured") {
                                            <div class="pane-row">
                                                <s25-ng-icon
                                                    [type]="row.detail === 'Contact' ? 'user' : 'users'"
                                                    [ariaLabel]="false"
                                                />
                                                <span class="pane-row-text">
                                                    <strong>{{ row.label }}</strong>
                                                    <span>{{ row.detail }}</span>
                                                </span>
                                            </div>
                                        }
                                    }
                                </li>
                            }
                        </ul>
                    }
                }

                @case ("action") {
                    @if (pane.action.spawnFormNames?.length) {
                        <dl class="pane-fields pane-spawns">
                            <dt>{{ pane.action.spawnFormNames!.length > 1 ? "Spawns forms" : "Spawns form" }}</dt>
                            <dd>{{ pane.action.spawnFormNames!.join(", ") }}</dd>
                        </dl>
                    }

                    <h4>Inputs</h4>
                    @if (inputRows().length) {
                        <dl class="pane-fields">
                            @for (row of inputRows(); track $index) {
                                <dt>{{ row.label }}</dt>
                                <dd [class.restricted]="row.restricted">
                                    @if (row.isJson) {
                                        <pre class="pane-pre">{{ row.value }}</pre>
                                    } @else {
                                        {{ row.value }}
                                    }
                                </dd>
                            }
                        </dl>
                    } @else {
                        <p class="pane-empty">No inputs</p>
                    }

                    @if (live()) {
                        @let execution = pane.action.detail?.execution;
                        <h4>Outcome</h4>
                        <dl class="pane-fields">
                            <dt>Status</dt>
                            <dd class="pane-outcome" [attr.data-status]="execution?.status">
                                {{ executionOutcomeLabel(execution) }}
                            </dd>
                            @if (execution?.executionDate) {
                                <dt>Run</dt>
                                <dd>{{ formatDate(execution.executionDate) }}</dd>
                            }
                            @if (execution?.httpResponseCode; as responseCode) {
                                <dt>Response code</dt>
                                <dd>{{ responseCode }}</dd>
                            }
                            @if (execution?.errorMessage) {
                                <dt>Error</dt>
                                <dd class="pane-wrap">{{ execution.errorMessage }}</dd>
                            }
                        </dl>
                    }

                    @if (request(); as httpRequest) {
                        <h4>Request</h4>
                        <dl class="pane-fields">
                            @if (httpRequest.method) {
                                <dt>Method</dt>
                                <dd>{{ httpRequest.method }}</dd>
                            }
                            @if (httpRequest.url) {
                                <dt>URL</dt>
                                <dd class="pane-wrap">{{ httpRequest.url }}</dd>
                            }
                            @if (httpRequest.payload) {
                                <dt>Payload</dt>
                                <dd>
                                    <pre class="pane-pre">{{ httpRequest.payload }}</pre>
                                </dd>
                            }
                        </dl>
                    }

                    @if (live() && pane.action.detail?.execution?.httpResponseBody; as body) {
                        <h4>Response</h4>
                        <pre class="pane-pre response-body" tabindex="0" role="region" aria-label="Response body">{{
                            body
                        }}</pre>
                    }
                }

                @case ("spawned") {
                    @if (!pane.group.forms.length) {
                        <p class="pane-empty">No forms spawned yet</p>
                    }
                    @for (form of pane.group.forms; track form.key) {
                        <section class="pane-spawned" [attr.aria-label]="form.name">
                            <h4>{{ form.name }}</h4>
                            <p class="pane-status" [attr.data-tone]="form.submitted ? 'approved' : 'waiting'">
                                <s25-ng-icon [type]="form.submitted ? 'check' : 'clock'" [ariaLabel]="false" />
                                {{ form.submitted ? "Submitted" : "Not submitted" }}
                                {{ form.currentStageName ? " · " + form.currentStageName : "" }}
                            </p>
                            @if (form.assignee; as assignee) {
                                <workflow-diagram-person
                                    [person]="assignee"
                                    [chatEnabled]="chatEnabled()"
                                    (chat)="emitChat(assignee, $event)"
                                />
                            }
                            @if (form.approvers.length) {
                                <h5>Approvers</h5>
                                <ul class="pane-list">
                                    @for (approver of form.approvers; track approver.key) {
                                        <li>
                                            <workflow-diagram-person
                                                [person]="approver"
                                                [chatEnabled]="chatEnabled()"
                                                [showStatus]="true"
                                                (chat)="emitChat(approver, $event)"
                                            />
                                        </li>
                                    }
                                </ul>
                            }
                        </section>
                    }
                }

                @case ("missing") {
                    <p class="pane-empty">This item is no longer part of the workflow. Select another item.</p>
                }
            }
        </div>
    `,
    host: {
        class: "workflow-diagram-detail-pane",
        "[class.is-dialog]": "mode() === 'dialog'",
        "[attr.id]": "paneId()",
        "[attr.role]": "mode() === 'dialog' ? 'dialog' : 'region'",
        "[attr.aria-modal]": "mode() === 'dialog' ? 'true' : null",
        "[attr.aria-labelledby]": "headingId()",
        "[style.height.px]": "sheetHeight()",
        "[style.max-height.px]": "sheetHeight()",
        "(keydown.escape)": "onEscape($event)",
        "(keydown.tab)": "trapFocus($any($event))",
    },
    styles: `
        :host {
            display: flex;
            flex-direction: column;
            min-width: 0;
            min-height: 0;
            background: var(--color--surface-bg);
            color: var(--color--text);
            border-left: 1px solid var(--color--border);
            overflow: hidden;
        }

        :host(.is-dialog) {
            position: absolute;
            inset: auto 0 0 0;
            max-height: 70%;
            border-left: none;
            border-top: 1px solid var(--color--border);
            border-radius: 16px 16px 0 0;
            box-shadow: 0 -8px 24px rgba(0, 0, 0, 0.2);
            z-index: 5;
        }

        .sheet-grabber {
            flex-shrink: 0;
            display: flex;
            align-items: center;
            justify-content: center;
            height: 24px;
            touch-action: none;
            cursor: ns-resize;
        }

        .sheet-grabber::before {
            content: "";
            width: 36px;
            height: 4px;
            border-radius: 2px;
            background: var(--color--border-bold);
        }

        .sheet-grabber:focus-visible {
            outline: 2px solid var(--color--link-text);
            outline-offset: -2px;
        }

        .pane-header {
            display: flex;
            align-items: flex-start;
            gap: 8px;
            padding: 12px 8px 12px 20px;
            border-bottom: 1px solid var(--color--border);
        }

        .pane-title {
            flex: 1;
            min-width: 0;
            display: flex;
            flex-direction: column;
            gap: 6px;
            padding-top: 2px;
        }

        .pane-eyebrow {
            display: flex;
            align-items: center;
            gap: 6px;
            margin: 0;
            font-size: 12px;
            font-weight: 600;
            color: var(--color--text-muted);
        }

        .pane-swatch {
            flex-shrink: 0;
            width: 12px;
            height: 12px;
            border-radius: 3px;
        }

        .pane-heading {
            margin: 0;
            font-size: 17px;
            line-height: 1.3;
            font-weight: 600;
            color: var(--color--text-strong);
            overflow-wrap: anywhere;
        }

        .pane-heading:focus {
            outline: none;
        }

        .pane-heading:focus-visible {
            outline: 2px solid var(--color--link-text);
            outline-offset: 2px;
        }

        .pane-chips {
            display: flex;
            flex-wrap: wrap;
            gap: 6px;
            margin: 0;
        }

        .pane-chip {
            display: inline-flex;
            align-items: center;
            padding: 2px 8px;
            border: 1px solid transparent;
            border-radius: 10px;
            background: var(--color--surface-2-bg);
            color: var(--color--text-strong);
            font-size: 12px;
            font-weight: 600;
        }

        .pane-chip[data-status="completed"] {
            background: var(--color--success-flat-bg);
        }

        .pane-chip[data-status="current"],
        .pane-chip[data-mode="live"] {
            background: var(--color--primary-flat-active-bg);
            color: var(--color--primary-flat-fg);
        }

        .pane-chip[data-status="changes_requested"] {
            background: var(--color--warning-flat-bg);
        }

        .pane-chip[data-status="denied"] {
            background: var(--color--danger-flat-bg);
        }

        .pane-chip[data-mode="configured"] {
            border: 1px dashed var(--color--border-bold);
        }

        .pane-rollup {
            display: flex;
            align-items: center;
            gap: 10px;
            margin: 0;
            font-size: 13px;
            font-weight: 600;
            color: var(--color--text-muted);
        }

        .pane-progress {
            flex: 1;
            display: flex;
            height: 6px;
            border-radius: 3px;
            background: var(--color--surface-2-bg);
            overflow: hidden;
        }

        .pane-progress-fill {
            background: var(--color--text-success);
        }

        .pane-close {
            flex-shrink: 0;
            width: 44px;
            height: 44px;
            display: inline-flex;
            align-items: center;
            justify-content: center;
            border: none;
            border-radius: 6px;
            background: transparent;
            color: var(--color--text-muted);
            cursor: pointer;
        }

        .pane-close:hover {
            background: var(--color--surface-2-bg);
            color: var(--color--text-strong);
        }

        .pane-close:focus-visible {
            outline: 2px solid var(--color--link-text);
        }

        .pane-body {
            flex: 1;
            min-height: 0;
            overflow: auto;
            padding: 4px 20px 24px;
        }

        h4 {
            margin: 22px 0 8px;
            font-size: 12px;
            font-weight: 600;
            text-transform: uppercase;
            letter-spacing: 0.5px;
            color: var(--color--text-muted);
        }

        h5 {
            margin: 0 0 8px;
            font-size: 13px;
            font-weight: 600;
            color: var(--color--text-strong);
        }

        .pane-list {
            list-style: none;
            margin: 0;
            padding: 0;
            display: flex;
            flex-direction: column;
            gap: 6px;
        }

        .pane-row {
            display: flex;
            align-items: center;
            gap: 8px;
            min-height: 36px;
        }

        .pane-row > s25-ng-icon {
            flex-shrink: 0;
            width: 36px;
            height: 36px;
            display: inline-flex;
            align-items: center;
            justify-content: center;
            border-radius: 50%;
            background: var(--color--surface-2-bg);
            color: var(--color--text-muted);
            --icon-size: 16px;
        }

        .pane-row-text {
            display: flex;
            flex-direction: column;
            min-width: 0;
            font-size: 13px;
            overflow-wrap: anywhere;
        }

        .pane-row-text strong {
            font-weight: 500;
            color: var(--color--text-strong);
        }

        .pane-row-text > span:not(.pane-status) {
            font-size: 12px;
            color: var(--color--text-muted);
        }

        .pane-status {
            display: inline-flex;
            align-items: center;
            gap: 4px;
            font-size: 12px;
            font-weight: 600;
            margin: 2px 0;
            color: var(--color--text-muted);
            --icon-size: 14px;
        }

        .pane-status[data-tone="approved"] {
            color: var(--color--text-success);
        }

        .pane-status[data-tone="denied"] {
            color: var(--color--text-danger);
        }

        .pane-status[data-tone="waiting"],
        .pane-status[data-tone="changes"] {
            color: var(--color--text-warning);
        }

        .pane-status[data-tone="deferred"],
        .pane-status[data-tone="configured"] {
            color: var(--color--link-text);
        }

        .pane-fields {
            display: grid;
            grid-template-columns: minmax(84px, max-content) minmax(0, 1fr);
            gap: 6px 12px;
            margin: 0;
            font-size: 13px;
        }

        .pane-fields dt {
            font-weight: normal;
            color: var(--color--text-muted);
        }

        .pane-fields dd {
            margin: 0;
            min-width: 0;
            overflow-wrap: anywhere;
        }

        .pane-fields dd.restricted {
            font-style: italic;
            color: var(--color--text-muted);
        }

        .pane-outcome {
            font-weight: 600;
        }

        .pane-outcome[data-status="COMPLETED"] {
            color: var(--color--text-success);
        }

        .pane-outcome[data-status="ERROR"] {
            color: var(--color--text-danger);
        }

        .pane-spawns {
            margin-top: 16px;
            padding: 10px 12px;
            border-radius: 8px;
            background: var(--color--surface-2-bg);
        }

        .pane-wrap {
            overflow-wrap: anywhere;
        }

        .pane-pre {
            margin: 0;
            padding: 8px 10px;
            border-radius: 6px;
            background: var(--color--surface-2-bg);
            font-size: 12px;
            line-height: 1.45;
            white-space: pre-wrap;
            overflow-wrap: anywhere;
        }

        .response-body {
            max-height: 240px;
            overflow: auto;
        }

        .response-body:focus-visible {
            outline: 2px solid var(--color--link-text);
        }

        .pane-link {
            width: 100%;
            min-height: 40px;
            display: flex;
            align-items: center;
            justify-content: space-between;
            gap: 8px;
            padding: 0 4px;
            border: none;
            border-bottom: 1px solid var(--color--border);
            border-radius: 0;
            background: none;
            color: var(--color--link-text);
            font: inherit;
            font-size: 13px;
            font-weight: 500;
            text-align: left;
            cursor: pointer;
            --icon-size: 14px;
        }

        .pane-link:hover {
            background: var(--color--primary-flat-bg);
            color: var(--color--link-text-hover);
        }

        .pane-link:focus-visible {
            outline: 2px solid var(--color--link-text);
            outline-offset: -2px;
        }

        .pane-list:has(.pane-link) {
            gap: 0;
        }

        .pane-empty {
            font-size: 13px;
            color: var(--color--text-muted);
        }

        .pane-state,
        .pane-spawned {
            padding: 10px 12px;
            border: 1px solid var(--color--border);
            border-radius: 8px;
        }

        .pane-state + .pane-state,
        .pane-spawned + .pane-spawned {
            margin-top: 8px;
        }

        .pane-spawned {
            display: flex;
            flex-direction: column;
            gap: 8px;
        }

        .pane-spawned:first-child {
            margin-top: 16px;
        }

        .pane-spawned h4 {
            margin: 0;
            font-size: 14px;
            text-transform: none;
            letter-spacing: normal;
            color: var(--color--text-strong);
        }

        .pane-spawned h5 {
            margin: 4px 0 0;
            font-size: 12px;
            text-transform: uppercase;
            letter-spacing: 0.5px;
            color: var(--color--text-muted);
        }
    `,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [S25IconComponent, WorkflowDiagramPersonComponent],
})
export class WorkflowDiagramDetailPaneComponent {
    content = input.required<DiagramPaneContent>();
    mode = input<DiagramPaneMode>("region");
    live = input(false);
    chatEnabled = input(false);
    names = input<ObjectNames>();
    /** Configured mapping only: full URL and payload template for full-FLS viewers; host only otherwise. */
    fullHttpDetail = input(false);
    paneId = input.required<string>();

    closed = output<void>();
    chatWith = output<DiagramChatRequest>();
    selectItem = output<DiagramSelection>();

    private readonly elementRef = inject<ElementRef<HTMLElement>>(ElementRef);
    private readonly headingRef = viewChild.required<ElementRef<HTMLElement>>("headingElement");

    protected readonly aiOutcomeDisplay = aiOutcomeDisplay;
    protected readonly executionOutcomeLabel = executionOutcomeLabel;

    protected readonly headingId = computed(() => `${this.paneId()}-heading`);
    protected readonly heading = computed(() => paneHeading(this.content()));

    protected readonly stageTypeLabel = computed(() => {
        const pane = this.content();
        return pane.kind === "stage" ? StageTypeStyles[pane.stage.type].label : "";
    });

    protected readonly stageColor = computed(() => {
        const pane = this.content();
        return pane.kind === "stage" ? StageTypeStyles[pane.stage.type].gradient[1] : null;
    });

    private readonly action = computed<DiagramActionRow | undefined>(() => {
        const pane = this.content();
        return pane.kind === "action" ? pane.action : undefined;
    });

    protected readonly actionIsResolved = computed(() => this.live() && !!this.action()?.detail);

    protected readonly inputRows = computed<DiagramInputRow[]>(() => {
        const action = this.action();
        if (!action) return [];
        if (this.actionIsResolved()) {
            return action.detail!.inputs.map((resolvedInput) => ({
                ...formatResolvedValue(resolvedInput, this.names()),
                label: inputLabel(action.code, resolvedInput.inputCode),
            }));
        }
        return (action.configured.inputs ?? []).map((configuredInput) => ({
            label: inputLabel(action.code, configuredInput.inputCode),
            value: configuredInput.inputValue || "Not set",
            restricted: false,
            isJson: false,
        }));
    });

    protected readonly request = computed<PaneRequest | undefined>(() => {
        const action = this.action();
        if (action?.type !== "HTTP_REQUEST") return undefined;
        if (this.actionIsResolved()) return action.detail!.httpRequest ?? {};
        const configured = action.configured;
        const full = this.fullHttpDetail();
        return {
            method: configured.httpMethod,
            url: full ? configured.httpUrl : urlHost(configured.httpUrl),
            payload: full ? configured.jsonPayloadTemplate : undefined,
        };
    });

    private readonly dateFormatResource = s25Resource({ loader: () => UserprefService.getS25DateTimeformat() });

    private readonly phoneMedia = window.matchMedia(PHONE_MEDIA_QUERY);
    protected readonly isPhone = signal(this.phoneMedia.matches);
    protected readonly resizable = computed(() => this.mode() === "dialog" && this.isPhone());
    /** Null keeps the stylesheet's default sheet height. */
    protected readonly sheetHeight = linkedSignal<boolean, number | null>({
        source: this.resizable,
        computation: () => null,
    });
    /** Measured after render, since the default height depends on the content. */
    protected readonly sheetPercent = signal(0);
    private stopGrabberDrag?: () => void;

    constructor() {
        afterRenderEffect(() => {
            this.content();
            if (this.mode() === "dialog") this.headingRef().nativeElement.focus();
        });

        afterRenderEffect(() => {
            this.content();
            this.sheetHeight();
            if (!this.resizable()) return;
            const viewerHeight = this.sheetLimits().viewerHeight;
            const height = this.elementRef.nativeElement.getBoundingClientRect().height;
            this.sheetPercent.set(viewerHeight ? Math.round((height / viewerHeight) * 100) : 0);
        });

        const onPhoneChange = (event: MediaQueryListEvent) => this.isPhone.set(event.matches);
        this.phoneMedia.addEventListener("change", onPhoneChange);
        inject(DestroyRef).onDestroy(() => {
            this.phoneMedia.removeEventListener("change", onPhoneChange);
            this.stopGrabberDrag?.();
        });
    }

    private sheetLimits() {
        const viewer = this.elementRef.nativeElement.offsetParent as HTMLElement | null;
        const viewerHeight = viewer?.clientHeight ?? 0;
        const max = Math.max(0, viewerHeight - SHEET_TOP_GAP_PX);
        const min = Math.min(max, Math.max(SHEET_MIN_HEIGHT_PX, viewerHeight * SHEET_MIN_FRACTION));
        return { viewerHeight, min, max, viewerBottom: viewer?.getBoundingClientRect().bottom ?? 0 };
    }

    private setSheetHeight(height: number) {
        const { min, max } = this.sheetLimits();
        this.sheetHeight.set(Math.round(Math.max(min, Math.min(height, max))));
    }

    private toggleSheetExpanded() {
        const { max } = this.sheetLimits();
        const height = this.sheetHeight();
        if (height != null && height >= max - 1) this.sheetHeight.set(null);
        else this.sheetHeight.set(max);
    }

    protected onGrabberPointerDown(event: PointerEvent) {
        event.preventDefault();
        this.stopGrabberDrag?.();
        const startY = event.clientY;
        let moved = false;
        const onMove = (moveEvent: PointerEvent) => {
            if (Math.abs(moveEvent.clientY - startY) >= TAP_SLOP_PX) moved = true;
            if (moved) this.setSheetHeight(this.sheetLimits().viewerBottom - moveEvent.clientY);
        };
        const onUp = () => {
            this.stopGrabberDrag?.();
            if (!moved) this.toggleSheetExpanded();
        };
        window.addEventListener("pointermove", onMove);
        window.addEventListener("pointerup", onUp);
        window.addEventListener("pointercancel", onUp);
        this.stopGrabberDrag = () => {
            window.removeEventListener("pointermove", onMove);
            window.removeEventListener("pointerup", onUp);
            window.removeEventListener("pointercancel", onUp);
            this.stopGrabberDrag = undefined;
        };
    }

    protected onGrabberKeyDown(event: KeyboardEvent) {
        const { viewerHeight, min, max } = this.sheetLimits();
        const current = this.sheetHeight() ?? this.elementRef.nativeElement.getBoundingClientRect().height;
        const step = viewerHeight * SHEET_KEY_STEP_FRACTION;
        switch (event.key) {
            case "ArrowUp":
                this.setSheetHeight(current + step);
                break;
            case "ArrowDown":
                this.setSheetHeight(current - step);
                break;
            case "Home":
                this.setSheetHeight(min);
                break;
            case "End":
                this.setSheetHeight(max);
                break;
            case "Enter":
            case " ":
                this.toggleSheetExpanded();
                break;
            default:
                return;
        }
        event.preventDefault();
        event.stopPropagation();
    }

    protected formatDate(date: string): string {
        const format = this.dateFormatResource.value();
        return (format && S25Datefilter.transform(date, format)) || date;
    }

    protected emitChat(person: DiagramPersonRow, trigger: HTMLElement) {
        const request = chatRequest(person, trigger);
        if (request) this.chatWith.emit(request);
    }

    protected onEscape(event: Event) {
        event.preventDefault();
        event.stopPropagation();
        this.closed.emit();
    }

    protected trapFocus(event: KeyboardEvent) {
        if (this.mode() !== "dialog") return;
        const focusable = Array.from(this.elementRef.nativeElement.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR));
        if (!focusable.length) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        const active = document.activeElement;
        const focusIsOutside = !this.elementRef.nativeElement.contains(active);
        if (event.shiftKey && (active === first || active === this.headingRef().nativeElement || focusIsOutside)) {
            event.preventDefault();
            last.focus();
        } else if (!event.shiftKey && (active === last || focusIsOutside)) {
            event.preventDefault();
            first.focus();
        }
    }
}
